/**
 * 「つなぐ」の段に出す、どの AI にも貼る 1 つの文（2026-10-11 console/one-connect）。純関数だけ（テストは webConnect.test.ts・画面は pages/HomeParts.tsx ConnectBody）。
 *
 * founder 2026-10-11:「ダッシュボードのプロンプトって面倒くさいから、ひとまとめでいいんじゃないの？ あとはエージェントがいい感じでやってくれるでしょ。
 *   人間はコピペするだけでいい。なんならドキュメントの URL を貼るだけで、エージェントが勝手にやってほしい」
 * 経緯: 2026-09-28 はパソコンの AI エージェントに貼る 1 行だけ（homeFlow.ts の connectAsk）→ Web・スマホの Claude・ChatGPT の人が止まった →
 *   2026-10-11 console/dash-web-connect で「Where do you use AI?」の 2 つのボタンで選ばせた（選んだ方を localStorage の kumo.console.connectWay に覚えた）→
 *   選ぶ手間をやめ、AI 自身に居場所を判断させる 1 つの文にした（このファイル）。connectAsk・選ぶボタン・覚える値は消した。
 * 文の中身: 「サイトの案内のページ（AI が読む /docs/connect/index.md）を読んで、自分に合う部分に従って」＋読めないときの事実（MCP の URL・connect の行）。
 */
// 文の正本はサイトの connectPrompt.mjs。ここに文を写さず直接 import する（二重に持つと片方だけ古くなる。WHY はそのファイルの頭）。
//   同じであることは webConnect.test.ts がサイトのビルドの作り（build.mjs sitePrompt と同じ入力）と比べて確かめる
import { connectGuidePath, oneConnectPrompt, previewConnectPrompt, webStepsPath } from '../../site/src/connectPrompt.mjs';
import { safeHttpUrl } from './homeFlow.ts';

/**
 * サイトの「はじめる」のページ（/docs/start/・2026-10-25 site/start-page・founder 10/25「初めての人には『はじめる』ページを 1 つ渡す」）の URL。
 * 「つなぐ」の段の貼る文の下に小さなリンクで出す（HomeParts.tsx ConnectBody）。貼る文は AI が読む案内（/docs/connect/）、こちらは人が読む環境ごとの手順。
 * サイトの起点（VITE_KUMO_SITE_URL）が無い・怪しいなら null = リンクを出さない（ドメインを推測で作らない）。
 * ja はサイトが日本語を隠しているビルドでも開ける（/ja/… は英語へ 301・apps/site の build.mjs）。slug はサイトの nav.mjs の 'start'
 */
export function startPageUrl(siteUrl: unknown, locale: 'en' | 'ja'): string | null {
  const site = safeHttpUrl(siteUrl);
  return site ? `${site}/${locale === 'ja' ? 'ja/' : ''}docs/start/` : null;
}

/**
 * 貼る文。URL はどれも設定から（ドメインを直書きしない）:
 *   siteUrl … サイトの起点（ビルドの VITE_KUMO_SITE_URL）→ 案内のページ（.md）の URL。無い・怪しいなら案内のページの行を出さない
 *   mcpUrl  … この Kumo の MCP の URL（/v1/platform/info → homeFlow.ts mcpEndpoint で検査済み）
 *   apiUrl  … この Kumo の API の公開 URL（/v1/platform/info）。connect --api に入れる = CLI のビルドの既定が無い環境でもこの Kumo につながる。
 *             無い・怪しいなら --api を付けない（CLI の既定の行き先）
 *   cli     … `npx <パッケージ>`（cliCommand.ts CLI）
 * 案内のページも MCP の URL も無いなら null（connect の行だけでは、どの環境につなぐのか分からない = 文を出さない）
 */
// ownGame（任意・2026-10-18 gtm/early-fixes）: 広告から来た人（adVisit.ts cameFromAd）。締めの頼みが「このゲームを公開して」になる（connectPrompt.mjs askLine）
export function connectPromptFor(input: { siteUrl: unknown; mcpUrl: string | null; apiUrl: unknown; cli: string; productName: string; locale: 'en' | 'ja'; ownGame?: boolean }): string | null {
  const site = safeHttpUrl(input.siteUrl);
  const mcpUrl = safeHttpUrl(input.mcpUrl);
  const api = safeHttpUrl(input.apiUrl);
  const guideUrl = site ? `${site}/${connectGuidePath(input.locale)}` : null;
  if (!guideUrl && !mcpUrl) return null;
  return oneConnectPrompt(
    { productName: input.productName, guideUrl, mcpUrl, command: api ? `${input.cli} connect --api ${api}` : `${input.cli} connect`, ownGame: input.ownGame === true },
    input.locale
  );
}

/**
 * Preview（KUMODeck Preview (v0.2)）につなぐ貼る文（2026-10-16 console/guide-preview・文は connectPrompt.mjs previewConnectPrompt の WHY）。
 * /guide の「AIをつなぐ」で、Preview が開いている（/v1/platform/info の previewMcpUrl）ときだけ connectPromptFor の代わりに使う。
 *   mcpUrl … previewMcpUrl を homeFlow.ts mcpEndpoint で /mcp まで足したもの。怪しい・無いなら null（呼ぶ側はいつもの文に戻す）
 *   siteUrl … サイトの起点（VITE_KUMO_SITE_URL）→ 手順のページ（/docs/claude-and-chatgpt/ の .md）の URL（2026-10-17 console/ai-does-connect）。
 *             無い・怪しいならその行を出さない（connectPromptFor の案内のページと同じ扱い）
 * 案内のページ・connect のコマンドは入れない（いつもの接続につながる）。コマンドで足すときの短い名前は productName から作る（例 kumodeck-preview）
 */
export function previewConnectPromptFor(input: { mcpUrl: string | null; siteUrl?: unknown; productName: string; previewName: string; locale: 'en' | 'ja'; ownGame?: boolean }): string | null {
  const mcpUrl = safeHttpUrl(input.mcpUrl);
  if (!mcpUrl) return null;
  const site = safeHttpUrl(input.siteUrl);
  const serverId = `${input.productName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'kumo'}-preview`;
  return previewConnectPrompt(
    { productName: input.productName, previewName: input.previewName, mcpUrl, serverId, stepsUrl: site ? `${site}/${webStepsPath(input.locale)}` : null, ownGame: input.ownGame === true },
    input.locale
  );
}

/**
 * 「つなぐ」の段（HomeParts.tsx ConnectBody = ホームの A の段・B の小さな 1 行・C・/guide の始め方 1）に出す貼る文を 1 つ選ぶ
 * （2026-10-16 console/home-preview・founder の決定「1 か所に本番とプレビューの両方があるのが間違い」）。
 *   platformLoading … /v1/platform/info をまだ読んでいる（previewMcpUrl があるか分からない）→ 'loading'（呼ぶ側は何も出さない。
 *                     いつもの文を一瞬出すと、それをコピーした人が Preview でない方につないでしまう）
 *   preview         … Preview の文（previewConnectPromptFor）。null = Preview が閉じている・URL が怪しい
 *   regular         … いつもの文（connectPromptFor）。Preview が無いときだけ使う（両方は出さない）
 * 読めなかった（エラー）ときは platformLoading = false・preview = null になり、いつもの文（Preview の有無が分からないので今までどおり）
 */
export function pickConnectPrompt(input: { platformLoading: boolean; preview: () => string | null; regular: () => string | null }): string | null | 'loading' {
  if (input.platformLoading) return 'loading';
  return input.preview() ?? input.regular();
}
